import React, { useEffect, useMemo, useState } from "react";

import { Clock, Heart, Loader2, Star, Store, Utensils } from "lucide-react";

import { useGlobalContext } from "../../Context/GlobalContext";
import { useRestaurantList } from "../../Services/useRestaurantList";
import useCart from "../../Services/useCart";
import { useCart as useLocalCart } from "../../Context/CartContext";
import RestaurantMenu from "../RestaurantMenu";
import { useNavigate } from "react-router-dom";
const isActiveStatus = (status) => {
  const normalizedStatus = String(status || "")
    .trim()
    .toUpperCase();

  return ["Y", "YES", "LIKE", "LIKED", "1", "TRUE"].includes(normalizedStatus);
};

const getFirstValue = (...values) => {
  return values.find((value) => {
    return value !== null && value !== undefined && String(value).trim() !== "";
  });
};

const formatPrice = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "";
  }

  return `₹${amount.toFixed(Number.isInteger(amount) ? 0 : 2)}`;
};

const getItemImage = (item) => {
  const rawItem = item?.raw || {};

  return String(
    getFirstValue(
      item?.itemImage,
      rawItem?.item_image,
      rawItem?.itemImage,
      rawItem?.image_url,
      rawItem?.imageUrl,
      rawItem?.photo,
      rawItem?.image,
      "",
    ) || "",
  )
    .trim()
    .replace(/^["']+|["']+$/g, "");
};

const getItemName = (item) => {
  const rawItem = item?.raw || {};

  return String(
    getFirstValue(
      item?.itemName,
      rawItem?.item_name,
      rawItem?.itemName,
      rawItem?.product_name,
      rawItem?.productName,
      rawItem?.name,
      "Food Item",
    ) || "Food Item",
  ).trim();
};

const getItemDescription = (item) => {
  const rawItem = item?.raw || {};

  return String(
    getFirstValue(
      rawItem?.item_description,
      rawItem?.itemDescription,
      rawItem?.product_description,
      rawItem?.productDescription,
      rawItem?.description,
      item?.restaurantName
        ? `From ${item.restaurantName}`
        : "Freshly prepared food item.",
    ) || "",
  ).trim();
};

const getItemPrice = (item) => {
  const rawItem = item?.raw || {};

  const firstVariant = Array.isArray(rawItem?.variant_list)
    ? rawItem.variant_list[0]
    : Array.isArray(item?.variants)
      ? item.variants[0]
      : null;

  const originalPrice = Number(firstVariant?.price || 0);
  const discountPrice = Number(firstVariant?.discount_price || 0);

  if (discountPrice > 0 && discountPrice < originalPrice) {
    return discountPrice;
  }

  return getFirstValue(
    originalPrice,
    rawItem?.selling_price,
    rawItem?.discount_price,
    rawItem?.item_price,
    rawItem?.price,
  );
};

const getItemOldPrice = (item) => {
  const rawItem = item?.raw || {};

  const firstVariant = Array.isArray(rawItem?.variant_list)
    ? rawItem.variant_list[0]
    : Array.isArray(item?.variants)
      ? item.variants[0]
      : null;

  const originalPrice = Number(firstVariant?.price || 0);
  const discountPrice = Number(firstVariant?.discount_price || 0);

  if (discountPrice > 0 && discountPrice < originalPrice) {
    return originalPrice;
  }

  return "";
};
const getRestaurantImage = (restaurant) => {
  const rawRestaurant = restaurant?.raw || {};

  return String(
    getFirstValue(
      restaurant?.imageUrl,
      restaurant?.brandImage,
      restaurant?.organizationLogo,
      rawRestaurant?.imageUrl,
      rawRestaurant?.image_url,
      rawRestaurant?.brand_image,
      rawRestaurant?.organization_logo,
      rawRestaurant?.restaurant_profile_image,
      "",
    ) || "",
  )
    .trim()
    .replace(/^["']+|["']+$/g, "");
};

const getRestaurantCategory = (restaurant) => {
  const rawRestaurant = restaurant?.raw || {};

  const servingType = String(
    rawRestaurant?.serving_type || rawRestaurant?.servingType || "",
  )
    .trim()
    .toUpperCase();

  if (servingType === "V") {
    return "Pure Veg";
  }

  if (servingType === "N") {
    return "Non Veg";
  }

  if (servingType === "B") {
    return "Veg & Non Veg";
  }

  return String(
    getFirstValue(
      rawRestaurant?.category_name,
      rawRestaurant?.categoryName,
      rawRestaurant?.cuisine,
      rawRestaurant?.description,
      "Restaurant",
    ) || "Restaurant",
  ).trim();
};

const getRestaurantRating = (restaurant) => {
  const rawRestaurant = restaurant?.raw || {};

  const rating = Number(
    getFirstValue(
      rawRestaurant?.rating,
      rawRestaurant?.ratings,
      rawRestaurant?.restaurant_rating,
      0,
    ),
  );

  if (!Number.isFinite(rating) || rating <= 0) {
    return null;
  }

  return rating.toFixed(1);
};

const getRestaurantTime = (restaurant) => {
  const rawRestaurant = restaurant?.raw || {};

  const deliveryTime = getFirstValue(
    rawRestaurant?.delivery_time,
    rawRestaurant?.deliveryTime,
    rawRestaurant?.estimated_delivery_time,
  );

  if (
    deliveryTime === null ||
    deliveryTime === undefined ||
    String(deliveryTime).trim() === ""
  ) {
    return null;
  }

  const timeText = String(deliveryTime).trim();

  return timeText.toLowerCase().includes("min") ? timeText : `${timeText} mins`;
};

export default function Favourite() {
  const { user } = useGlobalContext();
  const { createCartSequentially } = useCart();
  const { addToCart, increaseItem, decreaseItem, getQuantity } = useLocalCart();

  const {
    // Restaurant favourites
    likedRestaurantDetails,
    likedRestaurantLoading,
    likedRestaurantError,
    getLikeRestaurantDetails,
    likeDislikeRestaurant,

    // Item favourites
    likedItemDetails,
    likedItemLoading,
    likedItemError,
    getLikeDislikeItems,
    insertLikeDislikeItems,
  } = useRestaurantList();

  const [activeFilter, setActiveFilter] = useState("items");

  const [removingKeys, setRemovingKeys] = useState([]);

  const [locationResolved, setLocationResolved] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  const [coordinates, setCoordinates] = useState({
    latitude: "",
    longitude: "",
  });

  const customerId = String(
    user?.user_code ||
      user?.customer_id ||
      user?.customerId ||
      sessionStorage.getItem("user_code") ||
      "",
  ).trim();

const handleAddToCart = async (item) => {
  console.log("Clicked Item:", item);
  console.log("Variant:", item.variant);

  try {
    const mobile =
      sessionStorage.getItem("phone") || sessionStorage.getItem("number");

    console.log("Calling createCartSequentially...");

 const cartItem = {
   ...item.raw,

   prices: item.variant
     ? [
         {
           price: Number(item.variant.price || 0),
           discount_price: Number(item.variant.discount_price || 0),
           discount: Number(item.variant.discount || 0),
           size: item.variant.size || "Regular",
         },
       ]
     : [],
 };

const restaurantId =
  item.shopCode ||
  item.raw?.shop_code ||
  item.raw?.user_id ||
  item.raw?.fk_user_id ||
  "";

addToCart({
  item: cartItem,
  category: {
    category_name: "",
    cat_img_url: "",
  },
  restaurant: {
    userid: restaurantId,
    user_id: restaurantId,
    shop_code: restaurantId,
  },
});

    console.log("Item added successfully");
  } catch (error) {
    console.error("Add To Cart Error:", error);
  }
};


const navigate = useNavigate();

const handleRestaurantClick = (restaurant) => {
  navigate(`/restaurant-details/${restaurant.userId}`);
};

  /* =====================================================
     LOCATION
  ===================================================== */

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationResolved(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoordinates({
          latitude: String(position.coords.latitude),
          longitude: String(position.coords.longitude),
        });

        setLocationResolved(true);
      },
      () => {
        setCoordinates({
          latitude: "",
          longitude: "",
        });

        setLocationResolved(true);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000,
      },
    );
  }, []);

  /* =====================================================
     LOAD RESTAURANTS
  ===================================================== */

useEffect(() => {
  if (
  !customerId ||
  !locationResolved ||
  !coordinates.latitude ||
  !coordinates.longitude) {
    return;
  }

  getLikeRestaurantDetails({
    customerId,
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    range: "20",
  });
}, [
  customerId,
  coordinates.latitude,
  coordinates.longitude,
  getLikeRestaurantDetails,
  locationResolved,
]);

  /* =====================================================
     LOAD ITEMS
  ===================================================== */

 useEffect(() => {
   if (
  !customerId ||
  !locationResolved ||
  !coordinates.latitude ||
  !coordinates.longitude) {
     return;
   }

   getLikeDislikeItems({
     customerId,
     latitude: coordinates.latitude,
     longitude: coordinates.longitude,
     range: "20",
   });
 }, [
   customerId,
   coordinates.latitude,
   coordinates.longitude,
   getLikeDislikeItems,
   locationResolved,
 ]);

  /* =====================================================
     ITEM CARDS
  ===================================================== */

  const itemCards = useMemo(() => {
    return likedItemDetails
      .filter((item) => isActiveStatus(item?.itemStatus))
      .map((item, index) => {
        const rawItem = item?.raw || {};

        const itemId = String(
          item?.itemId || rawItem?.item_id || rawItem?.id || "",
        ).trim();

        const shopCode = String(
          item?.shopCode || rawItem?.shop_code || rawItem?.user_id || "",
        ).trim();

        const price = getItemPrice(item);

        const oldPrice = getItemOldPrice(item);
return {
  key: `item-${shopCode}-${itemId}-${index}`,
  id: itemId,
  type: "item",
  shopCode,
  image: getItemImage(item),
  name: getItemName(item),
  desc: getItemDescription(item),
  price: formatPrice(price),
  oldPrice:
    oldPrice && Number(oldPrice) !== Number(price) ? formatPrice(oldPrice) : "",

  raw: rawItem,
  variant: Array.isArray(rawItem?.variant_list)
    ? rawItem.variant_list[0]
    : null,
};
      });
  }, [likedItemDetails]);

  /* =====================================================
     RESTAURANT CARDS
  ===================================================== */

  const restaurantCards = useMemo(() => {
    return likedRestaurantDetails
      .filter((restaurant) => isActiveStatus(restaurant?.likeStatus))
      .map((restaurant, index) => {
        const rawRestaurant = restaurant?.raw || {};

        const userId = String(
          restaurant?.userId ||
            rawRestaurant?.fk_user_id ||
            rawRestaurant?.user_id ||
            "",
        ).trim();

        return {
          key: `restaurant-${userId}-${index}`,
          id: userId,
          userId,
          type: "restaurant",
          image: getRestaurantImage(restaurant),
          name: String(
            restaurant?.organizationName ||
              rawRestaurant?.organization_name ||
              rawRestaurant?.restaurant_name ||
              "Restaurant",
          ).trim(),
          category: getRestaurantCategory(restaurant),
          rating: getRestaurantRating(restaurant),
          time: getRestaurantTime(restaurant),
        };
      });
  }, [likedRestaurantDetails]);

  const favouriteItems = useMemo(() => {
    if (activeFilter === "items") {
      return itemCards;
    }

    if (activeFilter === "restaurants") {
      return restaurantCards;
    }

    return [...itemCards, ...restaurantCards];
  }, [activeFilter, itemCards, restaurantCards]);

  /* =====================================================
     REMOVE ITEM FAVOURITE
  ===================================================== */

  const removeItemFavourite = async (event, item) => {
    event.stopPropagation();

    if (!customerId || !item?.shopCode || !item?.id) {
      return;
    }

    if (removingKeys.includes(item.key)) {
      return;
    }

    setRemovingKeys((previous) => [...previous, item.key]);

    try {
      const result = await insertLikeDislikeItems({
        customerId,
        shopCode: item.shopCode,
        itemId: item.id,
        itemStatus: "N",
      });

      if (!result?.success) {
        console.error(result?.message);
      }
    } finally {
      setRemovingKeys((previous) => previous.filter((key) => key !== item.key));
    }
  };

  /* =====================================================
     REMOVE RESTAURANT FAVOURITE
  ===================================================== */

  const removeRestaurantFavourite = async (event, restaurant) => {
    event.stopPropagation();

    if (!customerId || !restaurant?.userId) {
      return;
    }

    if (removingKeys.includes(restaurant.key)) {
      return;
    }

    setRemovingKeys((previous) => [...previous, restaurant.key]);

    try {
      const result = await likeDislikeRestaurant({
        customerId,
        userId: restaurant.userId,
        status: "N",
      });

      if (!result?.success) {
        console.error(result?.message);
      }
    } finally {
      setRemovingKeys((previous) =>
        previous.filter((key) => key !== restaurant.key),
      );
    }
  };

  const isLoading =
    likedRestaurantLoading || likedItemLoading || !locationResolved;

  const errorMessage = likedRestaurantError || likedItemError;

  if (selectedRestaurant) {
    return (
      <RestaurantMenu
        userid={selectedRestaurant.userid}
        orderType={selectedRestaurant.orderType}
        platform={selectedRestaurant.platform}
      />
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white h-[calc(100vh-120px)] flex flex-col overflow-hidden">
      {/* Header */}

      {/* Fixed Header */}
      <div className="shrink-0 border-b border-gray-100 bg-white px-6 py-5">
        <h2 className="text-[28px] font-semibold text-gray-800">Favorite</h2>

        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={() => setActiveFilter("items")}
            className={`rounded-full border px-5 py-2 text-sm font-medium ${
              activeFilter === "items"
                ? "border-[#1F1A56] bg-[#1F1A56] text-white"
                : "bg-gray-50 text-gray-700"
            }`}
          >
            Items: {itemCards.length}
          </button>

          <button
            type="button"
            onClick={() => setActiveFilter("restaurants")}
            className={`rounded-full border px-5 py-2 text-sm font-medium ${
              activeFilter === "restaurants"
                ? "border-[#1F1A56] bg-[#1F1A56] text-white"
                : "bg-gray-50 text-gray-700"
            }`}
          >
            Restaurant: {restaurantCards.length}
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto hide-scrollbar px-6 py-6">
        {isLoading ? (
          <div className="flex min-h-[260px] items-center justify-center">
            <Loader2 size={34} className="animate-spin text-[#1F1A56]" />
          </div>
        ) : errorMessage && favouriteItems.length === 0 ? (
          <div className="flex min-h-[260px] items-center justify-center text-center text-sm text-red-500">
            {errorMessage}
          </div>
        ) : favouriteItems.length === 0 ? (
          <div className="flex min-h-[260px] flex-col items-center justify-center text-center">
            <Heart size={38} className="text-gray-300" />

            <p className="mt-3 font-medium text-gray-700">
              No favourites found
            </p>
          </div>
        ) : (
          /* Grid */

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {favouriteItems.map((item) => {
              const isRemoving = removingKeys.includes(item.key);
              const quantity = item.type === "item" ? getQuantity(item.id) : 0;

              return (
                <div
                  key={item.key}
                  onClick={() =>
                    item.type === "restaurant" && handleRestaurantClick(item)
                  }
                  className="overflow-hidden rounded-2xl border border-gray-200 bg-white cursor-pointer transition hover:shadow-md"
                >
                  {/* Image */}

                  <div className="relative h-44 bg-gray-100">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="h-full w-full object-cover"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        {item.type === "item" ? (
                          <Utensils size={38} className="text-gray-300" />
                        ) : (
                          <Store size={38} className="text-gray-300" />
                        )}
                      </div>
                    )}

                    <button
                      type="button"
                      disabled={isRemoving}
                      onClick={(event) => {
                        if (item.type === "item") {
                          removeItemFavourite(event, item);
                        } else {
                          removeRestaurantFavourite(event, item);
                        }
                      }}
                      className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white text-red-500 shadow disabled:cursor-wait disabled:opacity-70"
                      aria-label={`Remove ${item.name} from favourites`}
                    >
                      {isRemoving ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Heart size={16} fill="currentColor" />
                      )}
                    </button>
                  </div>

                  {item.type === "item" ? (
                    /* Food card */

                    <div className="p-4">
                      <h3 className="text-lg font-semibold text-gray-800">
                        {item.name}
                      </h3>

                      <p className="mt-1 line-clamp-2 text-sm text-gray-400">
                        {item.desc}
                      </p>

                      <div className="mt-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-semibold text-[#1F1A56]">
                            {item.price}
                          </span>

                          {item.oldPrice && (
                            <span className="text-sm text-gray-400 line-through">
                              {item.oldPrice}
                            </span>
                          )}
                        </div>

                        {quantity === 0 ? (
                          <button
                            type="button"
                            onClick={() => handleAddToCart(item)}
                            className="rounded-full bg-green-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-green-700"
                          >
                            ADD
                          </button>
                        ) : (
                          <div
                            className="flex items-center overflow-hidden rounded-full bg-green-600 text-white"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={() => decreaseItem(item.id)}
                              className="flex h-9 w-9 items-center justify-center text-lg font-bold hover:bg-green-700"
                            >
                              −
                            </button>

                            <span className="min-w-[30px] text-center text-sm font-semibold">
                              {quantity}
                            </span>

                            <button
                              type="button"
                              onClick={() => increaseItem(item.id)}
                              className="flex h-9 w-9 items-center justify-center text-lg font-bold hover:bg-green-700"
                            >
                              +
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* Restaurant card */

                    <div className="p-4">
                      <h3 className="text-lg font-semibold text-gray-800">
                        {item.name}
                      </h3>

                      <p className="mt-1 text-sm text-gray-400">
                        {item.category}
                      </p>

                      <div className="mt-4 flex items-center gap-3">
                        {/* Rating - only show if available */}
                        {item.rating && (
                          <span className="flex items-center gap-1 rounded-full bg-green-600 px-2 py-1 text-xs text-white">
                            <Star size={12} fill="white" />
                            {item.rating}
                          </span>
                        )}

                        {/* Timing - only show if available */}
                        {item.time && (
                          <span className="flex items-center gap-1 text-sm text-gray-400">
                            <Clock size={14} />
                            {item.time}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
